import { forwardRef, useState } from 'react';

import AvatarUpload from '@src/design-system/molecules/avatar-upload';
import { AvatarCropDialog } from '@src/design-system/molecules/avatar-crop-dialog';

type AvatarUploadWithCropProps = {
  name: string;
  onCropped: (payload: {
    file: File;
    previewUrl: string;
  }) => void | Promise<void>;
  allowedFormats?: string;
  maxSize?: string;
  multipleFile?: boolean;
  className?: string;
};

export const AvatarUploadWithCrop = forwardRef<
  HTMLDivElement,
  AvatarUploadWithCropProps
>(
  (
    {
      name,
      onCropped,
      allowedFormats = '.jpg, .png, .svg',
      maxSize = '800x400px',
      multipleFile = false,
      className,
    },
    ref
  ) => {
    const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleFileUpload = (file: File | null) => {
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          setCropImageSrc(reader.result);
          setIsCropOpen(true);
        }
      };
      reader.readAsDataURL(file);
    };

    const handleCropConfirm = async (file: File) => {
      const modifiedAvatar = new File([file], file.name.substring(0, 99), {
        type: file.type,
      });

      const previewUrl = URL.createObjectURL(modifiedAvatar);

      try {
        setIsSaving(true);
        await onCropped({ file: modifiedAvatar, previewUrl });
        setIsCropOpen(false);
        setCropImageSrc(null);
      } finally {
        setIsSaving(false);
      }
    };

    const handleClose = () => {
      if (isSaving) return;
      setIsCropOpen(false);
      setCropImageSrc(null);
    };

    return (
      <>
        <AvatarUpload
          name={name}
          ref={ref}
          onChange={handleFileUpload}
          allowedFormats={allowedFormats}
          maxSize={maxSize}
          multipleFile={multipleFile}
          className={className}
        />
        <AvatarCropDialog
          open={isCropOpen}
          imageSrc={cropImageSrc}
          onClose={handleClose}
          onConfirm={handleCropConfirm}
          loading={isSaving}
        />
      </>
    );
  }
);

AvatarUploadWithCrop.displayName = 'AvatarUploadWithCrop';
